import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { useAuth } from "@/context/AuthContext";

export function Dashboard() {
  const { user } = useAuth();
  
  // Fetch inquiries for the logged-in user only
  const { data: inquiries, isLoading } = useQuery({
    queryKey: ['inquiries', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data } = await supabase
        .from('inquiry')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      return data || [];
    },
    enabled: !!user
  });

  // Calculate inquiry stats
  const totalInquiries = inquiries?.length || 0;

  // Calculate inquiry stats by status
  const newInquiries = inquiries?.filter(i => i.status === 'new')?.length || 0;
  const confirmedInquiries = inquiries?.filter(i => i.status === 'confirmed')?.length || 0;
  const draftInquiries = inquiries?.filter(i => i.status === 'draft')?.length || 0;
  const sentInquiries = inquiries?.filter(i => i.status === 'sent')?.length || 0;
  
  // Prepare chart data for inquiries by status
  const inquiryStatusData = [
    { name: 'New', total: newInquiries },
    { name: 'Confirmed', total: confirmedInquiries },
    { name: 'Draft', total: draftInquiries },
    { name: 'Sent', total: sentInquiries }
  ];

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>
      
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <p>Loading inquiry data...</p>
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Inquiry Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex flex-col items-center justify-center bg-slate-100 p-4 rounded-lg">
                  <span className="text-sm text-muted-foreground">Total</span>
                  <span className="text-3xl font-bold">{totalInquiries}</span>
                </div>
                <div className="flex flex-col items-center justify-center bg-blue-100 p-4 rounded-lg">
                  <span className="text-sm text-blue-700">New</span>
                  <span className="text-3xl font-bold text-blue-700">{newInquiries}</span>
                </div>
                <div className="flex flex-col items-center justify-center bg-green-100 p-4 rounded-lg">
                  <span className="text-sm text-green-700">Confirmed</span>
                  <span className="text-3xl font-bold text-green-700">{confirmedInquiries}</span>
                </div>
                <div className="flex flex-col items-center justify-center bg-amber-100 p-4 rounded-lg">
                  <span className="text-sm text-amber-700">Draft</span>
                  <span className="text-3xl font-bold text-amber-700">{draftInquiries}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Inquiry Status Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={inquiryStatusData}>
                    <XAxis
                      dataKey="name"
                      stroke="#888888"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="#888888"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Bar
                      dataKey="total"
                      fill="#adfa1d"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
